import { redirect } from "@tanstack/solid-router";
import { businessesResource } from "~/features/business/business.resource";
import type { getContext } from "~/integrations/tanstack-query/provider";

type RouterContext = ReturnType<typeof getContext>;

/**
 * Throws a redirect if no business is currently selected.
 * Use in `beforeLoad` for any route that requires an active business.
 *
 * @param redirectTo - Where to redirect when no business is active.
 *   - Feature routes (Accounts, Documents, etc.): `/dashboard/business`
 *   - The business route itself: `/dashboard`
 *
 * @example
 * beforeLoad: ({ context }) => requireActiveBusiness(context),
 */
export async function requireActiveBusiness(
	context: RouterContext,
	redirectTo: string = "/dashboard/business",
) {
	const collection = await context.queryClient.ensureQueryData(
		businessesResource.options(undefined),
	);
	if (!collection.activeBusinessId) {
		throw redirect({ to: redirectTo });
	}
	return collection;
}

/**
 * Returns a `{ beforeLoad }` object that injects the active-business guard.
 * Spread this into a route config instead of writing the beforeLoad manually.
 *
 * Keeping it separate from `loader` and `component` preserves TypeScript's
 * type inference on the rest of the route config.
 *
 * @param redirectTo - Where to redirect when no business is active. Defaults to
 *                    `/dashboard/business`. Pass `/dashboard` for the business
 *                    route itself.
 *
 * @example
 * export const Route = createFileRoute("/_dashboard/dashboard/accounts")({
 *   ...withBusinessGuard(),
 *   loader: async ({ context }) => { ... },
 *   component: AccountsPage,
 * });
 *
 * // For the business route itself:
 * export const Route = createFileRoute("/_dashboard/dashboard/business")({
 *   ...withBusinessGuard("/dashboard"),
 *   loader: async ({ context }) => { ... },
 *   component: BusinessPage,
 * });
 */
export function withBusinessGuard(redirectTo = "/dashboard/business") {
	return {
		beforeLoad: ({ context }: { context: RouterContext }) =>
			requireActiveBusiness(context, redirectTo),
	};
}
